import { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, UserRound, LogOut } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { isExternal, mediaUrl } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function NavItem({ item, onClick, mobile }) {
  const cls = mobile
    ? "block rounded-2xl px-5 py-4 text-xl font-heading text-bt-heading hover:bg-bt-sage"
    : "rounded-full px-4 py-2 text-[15px] font-medium text-bt-heading transition-colors hover:bg-bt-sage";
  const tid = `nav-link-${slug(item.label)}${mobile ? "-mobile" : ""}`;
  if (isExternal(item.url))
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick} data-testid={tid}>
        {item.label}
      </a>
    );
  return (
    <NavLink to={item.url} end={item.url === "/"} onClick={onClick} data-testid={tid}
      className={({ isActive }) => `${cls} ${isActive && !mobile ? "bg-bt-sage" : ""}`}>
      {item.label}
    </NavLink>
  );
}

export default function Header() {
  const { settings } = useSite();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const signOut = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };
  const accountUrl = user ? "/account" : "/login";
  const label = settings.account_label || "My Account";

  return (
    <>
    <header className="sticky top-0 z-40 print:hidden border-b border-bt-border bg-bt-bg/90 backdrop-blur-md" style={{ backgroundColor: "color-mix(in srgb, var(--bt-background) 88%, transparent)" }}>
      <div className="container-bt flex h-[72px] items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={mediaUrl(settings.brand.logo_url)} alt={settings.brand.site_name} className="h-9 w-auto" />
          <span className="font-heading text-xl font-semibold text-bt-heading">{settings.brand.site_name}</span>
        </Link>
        <nav className="hidden items-center gap-1 lg:flex" data-testid="main-nav">
          {settings.nav.map((n) => <NavItem key={n.label} item={n} />)}
          <Link to={accountUrl} className="btn-primary ml-3 !min-h-[44px] !px-5 !py-2 text-sm" data-testid="nav-account-button">
            <UserRound size={16} /> {label}
          </Link>
          {user && (
            <button onClick={signOut} className="ml-1 inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-sm font-medium text-bt-heading transition-colors hover:bg-bt-sage" data-testid="nav-signout-button">
              <LogOut size={16} /> Sign out
            </button>
          )}
        </nav>
        <button className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-bt-sage lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button">
          <Menu className="text-bt-heading" />
        </button>
      </div>
    </header>
      {open && (
        <div className="fixed inset-0 z-[100] overflow-y-auto lg:hidden" style={{ backgroundColor: "#FFFFFF" }} data-testid="mobile-menu">
          <div className="container-bt flex h-[72px] items-center justify-between">
            <span className="font-heading text-xl font-semibold text-bt-heading">{settings.brand.site_name}</span>
            <button className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-bt-sage" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
              <X className="text-bt-heading" />
            </button>
          </div>
          <nav className="container-bt mt-6 flex flex-col gap-1">
            {settings.nav.map((n) => <NavItem key={n.label} item={n} mobile onClick={() => setOpen(false)} />)}
            <Link to={accountUrl} onClick={() => setOpen(false)} className="btn-primary mt-6 w-full" data-testid="mobile-account-button">
              <UserRound size={18} /> {label}
            </Link>
            {user && (
              <button onClick={signOut} className="btn-secondary mt-3 w-full" data-testid="mobile-signout-button">
                <LogOut size={18} /> Sign out
              </button>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
